/**
 * Shared homepage search option helpers.
 * Categories come from marketplace config; locations from landing page settings.
 */

export const QUICK_FILTER_OPTIONS = [
  { label: 'In Person', value: 'In-Person' },
  { label: 'Online', value: 'Online' },
  { label: 'Camps', value: 'Camp' },
];

/**
 * @param {Array<{ address?: string, lat?: number|null, lng?: number|null }>} rawLocations
 * @returns {Array<{ label: string, value: string, address: string, lat: number|null, lng: number|null }>}
 */
export const mapLandingPageLocations = (rawLocations = []) => {
  if (!Array.isArray(rawLocations)) {
    return [];
  }

  return rawLocations
    .filter(loc => loc && typeof loc.address === 'string' && loc.address.trim().length > 0)
    .map(loc => {
      const address = loc.address.trim();
      return {
        label: address,
        value: address,
        address,
        lat: typeof loc.lat === 'number' ? loc.lat : null,
        lng: typeof loc.lng === 'number' ? loc.lng : null,
      };
    });
};

/**
 * @param {Array<{ id: string, name: string, subcategories?: Array }>} listingCategories
 */
export const mapCategoryOptions = (listingCategories = []) => {
  if (!Array.isArray(listingCategories)) {
    return [];
  }

  return listingCategories.map(cat => ({
    label: cat.name,
    value: cat.id,
    id: cat.id,
    name: cat.name,
  }));
};

/**
 * @param {Array<{ id: string, name: string, subcategories?: Array }>} listingCategories
 * @param {string} categoryId
 */
export const getSubcategoryOptionsForCategory = (listingCategories = [], categoryId) => {
  if (!categoryId || !Array.isArray(listingCategories)) {
    return [];
  }

  const category = listingCategories.find(c => c.id === categoryId);
  const subcategories = category?.subcategories || [];

  return subcategories.map(sub => ({
    label: sub.name,
    value: sub.id,
    id: sub.id,
    name: sub.name,
    categoryId,
  }));
};

/**
 * Build a subcategories-by-category map keyed by category id.
 */
export const buildSubcategoriesByCategory = (listingCategories = []) => {
  if (!Array.isArray(listingCategories)) {
    return {};
  }

  return listingCategories.reduce((acc, cat) => {
    acc[cat.id] = getSubcategoryOptionsForCategory(listingCategories, cat.id);
    return acc;
  }, {});
};

const DEFAULT_BOUNDS_DELTA = 0.12;

/**
 * Create map bounds around a lat/lng point (for location-based search).
 */
export const createBoundsFromLatLng = (lat, lng, delta = DEFAULT_BOUNDS_DELTA) => {
  if (typeof lat !== 'number' || typeof lng !== 'number' || Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }

  return {
    ne: { lat: lat + delta, lng: lng + delta },
    sw: { lat: lat - delta, lng: lng - delta },
  };
};

/**
 * Serialize bounds object to the string format expected by landing-page APIs.
 */
export const serializeBounds = bounds => {
  if (!bounds) {
    return '';
  }

  if (typeof bounds === 'string') {
    return bounds;
  }

  const { ne, sw } = bounds;
  if (!ne || !sw) {
    return '';
  }

  return `${ne.lat},${ne.lng},${sw.lat},${sw.lng}`;
};

/**
 * Format a date for search without UTC day-shift (uses local calendar date).
 * @param {string|Date} dateValue
 * @returns {string|undefined} YYYY-MM-DD
 */
export const formatLocalDateYMD = dateValue => {
  if (!dateValue) {
    return undefined;
  }

  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }

  const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(d.getTime())) {
    return undefined;
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Find a configured location by address (case-insensitive trim match).
 */
export const findConfiguredLocation = (locations, address) => {
  if (!address || !Array.isArray(locations)) {
    return null;
  }

  const normalized = address.trim().toLowerCase();
  return locations.find(loc => loc.address?.trim().toLowerCase() === normalized) || null;
};
